import { Controller } from '@hotwired/stimulus';
import { DiceInputNotation } from '../../lib/rolls/input_notation';

export default class extends Controller {
  static targets = ['bonus', 'dieAdjustment', 'dieControl', 'favouriteLink', 'input', 'modifier', 'modifierControl'];

  static values = { favouriteUrl: String };

  connect() {
    this.refresh();
  }

  adjustDie(event) {
    event.preventDefault();
    this.#update((notation) =>
      notation.adjustDie(event.currentTarget.dataset.sides, event.currentTarget.dataset.amount)
    );
  }

  adjustBonus(event) {
    event.preventDefault();
    this.#update((notation) => notation.adjustBonus(event.currentTarget.dataset.amount));
  }

  toggleModifier(event) {
    event.preventDefault();
    this.#update((notation) =>
      notation.toggleModifier(event.currentTarget.dataset.sides, event.currentTarget.dataset.modifier)
    );
  }

  clear(event) {
    event.preventDefault();
    this.inputTarget.value = '';
    this.#inputChanged();
  }

  favourite(event) {
    event.preventDefault();
    if (!this.hasFavouriteUrlValue || !this.hasFavouriteLinkTarget) return;

    const url = new URL(this.favouriteUrlValue, window.location.origin);
    url.searchParams.set('favourite_roll[dice]', this.inputTarget.value);
    this.favouriteLinkTarget.href = url.toString();
    this.favouriteLinkTarget.click();
  }

  refresh() {
    const notation = DiceInputNotation.parse(this.inputTarget.value);

    this.dieControlTargets.forEach((control) => {
      const quantity = notation.die(control.dataset.sides)?.quantity || 0;
      this.#setContentEmphasis(control, quantity === 0);
    });

    this.dieAdjustmentTargets.forEach((button) => {
      const quantity = notation.die(button.dataset.sides)?.quantity || 0;
      const direction = Number(button.dataset.amount);
      const active = direction > 0 ? quantity > 0 : quantity < 0;

      button.classList.toggle('active', active);
      button.setAttribute('aria-pressed', String(active));
      button.querySelector('.quad-button__item-content').textContent = active
        ? this.#signedValue(quantity)
        : direction > 0
          ? '+'
          : '-';
    });

    this.modifierTargets.forEach((button) => {
      const die = notation.die(button.dataset.sides);
      const enabled = Math.abs(die?.quantity || 0) >= 2;
      const active = enabled && die.modifier === button.dataset.modifier;

      button.disabled = !enabled;
      button.classList.toggle('active', active);
      button.setAttribute('aria-pressed', String(active));
    });

    this.bonusTarget.textContent = this.#signedValue(notation.bonus);
    this.#setContentEmphasis(this.modifierControlTarget, notation.bonus === 0);
  }

  #update(callback) {
    const notation = DiceInputNotation.parse(this.inputTarget.value);
    this.inputTarget.value = callback(notation).toString();
    this.#inputChanged();
  }

  #inputChanged() {
    this.inputTarget.dispatchEvent(new Event('input', { bubbles: true }));
  }

  #signedValue(value) {
    return value > 0 ? `+${value}` : String(value);
  }

  #setContentEmphasis(control, muted) {
    control.classList.toggle('quad-button--content-muted', muted);
    control.classList.toggle('quad-button--content-normal', !muted);
  }
}
